import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
// @material-ui/core components
import { Card, CardContent, CardHeader, Menu, MenuItem } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import FormGroup from "@material-ui/core/FormGroup";
import FormLabel from "@material-ui/core/FormLabel";
import Grid from "@material-ui/core/Grid";
import InputAdornment from "@material-ui/core/InputAdornment";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import { makeStyles } from "@material-ui/core/styles";
// @material-ui/icons
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import SearchIcon from "@material-ui/icons/Search";
// components
import GenericButton from "components/Buttons/GenericButton";
//import custom components
import componentStylesGeneric from "assets/theme/views/admin/generic.js";
import FormSelect from "components/FormFields/FormSelect";
import useFilterSync from "hooks/useFilterSync";
import { getUserColectiveOpportunityApplicationsMap, opportunityApplicationsFollowUpExportReportMap } from "mappers/GetFilteredOpportunitiesMap";
import { getDataSetByNameAction } from "redux/actions/datasetsAction";
import { getUserColectiveOpportunityApplicationsAction } from "redux/actions/opportunitiesAction";
import getReportAction from "redux/actions/reportsAction";
import { DATASET, OPPORTUNITY_TYPE_KEY, PAGINATION, USER_TYPE } from "utils/const";
import { getDatasetStateByName } from "utils/datasets";
import { getSelectOptionsElementByCode } from "utils/formSelect";

const useStylesGeneric = makeStyles(componentStylesGeneric);

const useStylesLocal = makeStyles((theme) => ({
  actionButtonsContainer: {
    [theme.breakpoints.down("xs")]: {
      display: "flex",
      justifyContent: "flex-end",
      flexWrap: "wrap",
      gap: theme.spacing(1),
      flexDirection: "column",
      alignItems: "stretch",
      "& .MuiButton-root": {
        width: "100%",
      },
    },
  },
}));

function OpportunityApplicationsFilterSection(props) {
  const classes = useStylesGeneric();
  const classesLocal = useStylesLocal();
  const submittedFilterRef = useRef(null);

  const {
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    control,
    formState: { errors },
  } = useFormContext();

  const pageIndex = watch("pageIndex") ?? PAGINATION.FIRST_PAGE;
  const pageSize = watch("pageSize") ?? PAGINATION.PAGE_SIZE;

  const filterSync = useFilterSync({
    defaultValues: props.defaultFormValues || {
      applicationCode: "",
      opportunityType: null,
      searchText: "",
      applicationState: null,
      opportunityReference: "",
      userNameOrIdentityCard: "",
      pageIndex: PAGINATION.FIRST_PAGE,
      pageSize: PAGINATION.PAGE_SIZE,
    },
    syncPagination: true,
    pagination: {
      pageIndexKey: "pageIndex",
      pageSizeKey: "pageSize",
      defaultPageSize: props.defaultFormValues?.pageSize ?? PAGINATION.PAGE_SIZE,
    },
    onFilterChange: () => {
      // Quando filtros vêm da URL (mount/back-forward), libera busca.
      props.setFiltersReady?.(true);
    },
    fieldSerializers: {
      opportunityType: {
        toUrl: (value) => value?.code ?? null,
        fromUrl: (value) => value,
      },
      applicationState: {
        toUrl: (value) => value?.code ?? null,
        fromUrl: (value) => value,
      },
    },
  });

  useEffect(() => {
    if (
      props.datasetsState[DATASET.OPPORTUNITY_TYPE] === undefined ||
      props.datasetsState[DATASET.OPPORTUNITY_TYPE]?.length === 0
    ) {
      props.getDataSetByName(DATASET.OPPORTUNITY_TYPE);
    }
    if (
      props.datasetsState[DATASET.OPPORTUNITY_CANDIDATE_STATE] === undefined ||
      props.datasetsState[DATASET.OPPORTUNITY_CANDIDATE_STATE]?.length === 0
    ) {
      props.getDataSetByName(DATASET.OPPORTUNITY_CANDIDATE_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore select objects when options lists are ready (URL stores codes)
  useEffect(() => {
    const options = getDatasetStateByName(props, DATASET.OPPORTUNITY_TYPE);
    const current = getValues("opportunityType");

    if (current && typeof current !== "object" && options?.length > 0) {
      const selected =
        options.find((o) => String(o?.code) === String(current)) ||
        getSelectOptionsElementByCode(options, parseInt(current, 10));
      if (selected) setValue("opportunityType", selected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.datasetsState[DATASET.OPPORTUNITY_TYPE]]);

  useEffect(() => {
    const options = getDatasetStateByName(props, DATASET.OPPORTUNITY_CANDIDATE_STATE);
    const current = getValues("applicationState");

    if (current && typeof current !== "object" && options?.length > 0) {
      const selected =
        options.find((o) => String(o?.code) === String(current)) ||
        getSelectOptionsElementByCode(options, parseInt(current, 10));
      if (selected) setValue("applicationState", selected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.datasetsState[DATASET.OPPORTUNITY_CANDIDATE_STATE]]);

  useEffect(() => {
    if (props.match?.params?.opportunityType === OPPORTUNITY_TYPE_KEY.EMPREGO_URL) {
      setValue("opportunityType", { code: 1, label: "Emprego", keyValue: OPPORTUNITY_TYPE_KEY.EMPREGO });
    }
    if (props.match?.params?.opportunityType === OPPORTUNITY_TYPE_KEY.ESTAGIO_PROFISSIONAL_URL) {
      setValue("opportunityType", {
        code: 2,
        label: "Estágio Profissional",
        keyValue: OPPORTUNITY_TYPE_KEY.ESTAGIO_PROFISSIONAL,
      });
    }
    setValue("pageIndex", PAGINATION.FIRST_PAGE);
    handleSubmit(onSubmit)();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.match?.params?.opportunityType]);

  const cleanFilter = () => {
    const nextFormValues = {
      applicationCode: "",
      opportunityType: null,
      searchText: "",
      applicationState: null,
      opportunityReference: "",
      userNameOrIdentityCard: "",
      pageIndex: PAGINATION.FIRST_PAGE,
      pageSize: PAGINATION.PAGE_SIZE,
    };

    reset(nextFormValues);

    filterSync.handleFilterReset();

    const next = {
      ...getUserColectiveOpportunityApplicationsMap(nextFormValues),
      pageIndex: PAGINATION.FIRST_PAGE,
      pageSize: PAGINATION.PAGE_SIZE,
      sortColumn: props.sortColumn,
      sortDirection: props.sortDirection,
    };

    submittedFilterRef.current = next;
    props.getUserColectiveOpportunityApplications(next);
  };

  useEffect(() => {
    if (props.filtersReady === false) return;

    if (!submittedFilterRef.current) {
      const values = getValues();
      submittedFilterRef.current = {
        ...getUserColectiveOpportunityApplicationsMap(values),
        pageIndex,
        pageSize,
        sortColumn: props.sortColumn,
        sortDirection: props.sortDirection,
      };
    } else {
      submittedFilterRef.current = {
        ...submittedFilterRef.current,
        pageIndex,
        pageSize,
        sortColumn: props.sortColumn,
        sortDirection: props.sortDirection,
      };
    }

    props.getUserColectiveOpportunityApplications(submittedFilterRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize, props.sortColumn, props.sortDirection, props.filtersReady]);

  // Mantém paginação sincronizada na URL
  useEffect(() => {
    if (props.filtersReady === false) return;
    filterSync.updatePagination(pageIndex, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize, props.filtersReady]);

  const onSubmit = (data) => {
    const nextFormValues = {
      ...data,
      pageIndex: PAGINATION.FIRST_PAGE,
      pageSize: data.pageSize ?? PAGINATION.PAGE_SIZE,
    };

    // Sincroniza filtros na URL e reseta paginação
    filterSync.handleFilterSubmit(nextFormValues);

    const next = {
      ...getUserColectiveOpportunityApplicationsMap(nextFormValues),
      pageIndex: PAGINATION.FIRST_PAGE,
      pageSize: nextFormValues.pageSize,
      sortColumn: props.sortColumn,
      sortDirection: props.sortDirection,
    };

    submittedFilterRef.current = next;

    if ((data.pageIndex ?? PAGINATION.FIRST_PAGE) === PAGINATION.FIRST_PAGE) {
      props.getUserColectiveOpportunityApplications(next);
    } else {
      setValue("pageIndex", PAGINATION.FIRST_PAGE);
    }
  };

  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);
  const exportMenuOpen = Boolean(exportMenuAnchorEl);

  const handleOpenExportMenu = (event) => {
    setExportMenuAnchorEl(event.currentTarget);
  };

  const handleCloseExportMenu = () => {
    setExportMenuAnchorEl(null);
  };

  const handleExportReport = (extension) => {
    handleCloseExportMenu();

    const filterToExport =
      submittedFilterRef.current ||
      {
        ...getUserColectiveOpportunityApplicationsMap(getValues()),
        pageIndex,
        pageSize,
        sortColumn: props.sortColumn,
        sortDirection: props.sortDirection,
      };

    props.getReport(
      opportunityApplicationsFollowUpExportReportMap(filterToExport),
      "Lista_de_Candidaturas_a_Ofertas_" + moment().format("YYYYMMDDHHmm") + extension
    );
  };

  return (
    <Card classes={{ root: classes.cardRoot }}>
      <CardHeader
        classes={{
          action: classes.cardHeaderAction,
          title: classes.cardHeader,
        }}
        title={"Pesquisa de Candidaturas"}
      ></CardHeader>
      <CardContent>
        <Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container>
              <Grid item xs={12} sm={6} md={4}>
                <FormGroup>
                  <FormLabel>Nº Candidatura</FormLabel>
                  <Controller
                    name="applicationCode"
                    control={control}
                    defaultValue=""
                    component={Box}
                    render={({ field: { onChange, name, value } }) => (
                      <OutlinedInput
                        name={name}
                        value={value}
                        onChange={(e) => {
                          onChange(e.target.value);
                        }}
                        fullWidth
                        autoComplete="off"
                        type="text"
                        placeholder="Pesquisar"
                        endAdornment={
                          <InputAdornment position="end">
                            <SearchIcon />
                          </InputAdornment>
                        }
                      />
                    )}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormGroup>
                  <FormLabel>Estado</FormLabel>
                  <FormSelect
                    control={control}
                    fieldName="applicationState"
                    selectOptions={getDatasetStateByName(props, DATASET.OPPORTUNITY_CANDIDATE_STATE)}
                    getValues={getValues}
                    setValue={setValue}
                    errors={errors}
                    classes={classes}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormGroup>
                  <FormLabel>Nº Oferta</FormLabel>
                  <Controller
                    name="opportunityReference"
                    control={control}
                    defaultValue=""
                    component={Box}
                    render={({ field: { onChange, name, value } }) => (
                      <OutlinedInput
                        name={name}
                        value={value}
                        onChange={(e) => {
                          onChange(e.target.value);
                        }}
                        fullWidth
                        autoComplete="off"
                        type="text"
                        placeholder="Pesquisar"
                        endAdornment={
                          <InputAdornment position="end">
                            <SearchIcon />
                          </InputAdornment>
                        }
                      />
                    )}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormGroup>
                  <FormLabel>Tipo de Oferta</FormLabel>
                  <FormSelect
                    control={control}
                    fieldName="opportunityType"
                    selectOptions={getDatasetStateByName(props, DATASET.OPPORTUNITY_TYPE)}
                    getValues={getValues}
                    setValue={setValue}
                    errors={errors}
                    classes={classes}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormGroup>
                  <FormLabel>Pesquisa (título/descrição)</FormLabel>
                  <Controller
                    name="searchText"
                    control={control}
                    defaultValue=""
                    component={Box}
                    render={({ field: { onChange, name, value } }) => (
                      <OutlinedInput
                        name={name}
                        value={value}
                        onChange={(e) => {
                          onChange(e.target.value);
                        }}
                        fullWidth
                        autoComplete="off"
                        type="text"
                        placeholder="Pesquisar"
                        endAdornment={
                          <InputAdornment position="end">
                            <SearchIcon />
                          </InputAdornment>
                        }
                      />
                    )}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormGroup>
                  <FormLabel>Nome ou Nº BI</FormLabel>
                  <Controller
                    name="userNameOrIdentityCard"
                    control={control}
                    defaultValue=""
                    component={Box}
                    render={({ field: { onChange, name, value } }) => (
                      <OutlinedInput
                        name={name}
                        value={value}
                        onChange={(e) => {
                          onChange(e.target.value);
                        }}
                        fullWidth
                        autoComplete="off"
                        type="text"
                        placeholder="Pesquisar"
                        endAdornment={
                          <InputAdornment position="end">
                            <SearchIcon />
                          </InputAdornment>
                        }
                      />
                    )}
                  />
                </FormGroup>
              </Grid>

              {/* Botões de Pesquisa */}

              <Grid item xs={12} md={12} classes={{ root: classes.textAlignRight }}>
                <Box className={classesLocal.actionButtonsContainer}>
                {props.authState.personalInformation?.userType === USER_TYPE.COLLECTIVE && (
                  <>
                    <GenericButton
                      typeSubmit={false}
                      color="primary"
                      onClick={handleOpenExportMenu}
                      disabled={props.opportunitiesState?.getUserColectiveOpportunityApplications_loading}
                    >
                      Exportar <KeyboardArrowDownIcon />
                    </GenericButton>
                    <Menu
                      anchorEl={exportMenuAnchorEl}
                      open={exportMenuOpen}
                      onClose={handleCloseExportMenu}
                      keepMounted
                    >
                      <MenuItem onClick={() => handleExportReport(".pdf")}>PDF</MenuItem>
                      <MenuItem onClick={() => handleExportReport(".xlsx")}>Excel</MenuItem>
                    </Menu>
                  </>
                )}
                <GenericButton
                  typeSubmit={false}
                  color="ghost"
                  onClick={() => {
                    cleanFilter();
                  }}
                >
                  Limpar Filtros
                </GenericButton>
                <GenericButton
                  typeSubmit={true}
                  color="primary"
                  loading={props.opportunitiesState?.getUserColectiveOpportunityApplications_loading}
                >
                  Pesquisar
                </GenericButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </CardContent>
    </Card>
  );
}

const mapStateToProps = (state) => ({ ...state });

const mapDispatchToProps = (dispatch) => ({
  getUserColectiveOpportunityApplications: (data) => dispatch(getUserColectiveOpportunityApplicationsAction(data)),
  getDataSetByName: (data, parentKeyValue) => dispatch(getDataSetByNameAction(data, parentKeyValue)),
  getReport: (data, reportFileName) => dispatch(getReportAction(data, reportFileName)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(OpportunityApplicationsFilterSection));
